import { useState } from 'react';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import Button from '../ui/Button';
import {
  AlertTriangle, Calendar, FileText, Shield, Scale,
  Clock, CheckCircle, X, Bell, Loader2
} from 'lucide-react';
import type { ComplianceAlert } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';

interface AlertListProps {
  alerts: ComplianceAlert[];
  onAlertUpdated: () => void;
}

const ALERT_TYPE_CONFIG: Record<string, {
  icon: typeof AlertTriangle;
  iconColor: string;
  bgColor: string;
}> = {
  license_expiry: {
    icon: Scale,
    iconColor: 'text-warning-600',
    bgColor: 'bg-warning-100',
  },
  document_expiry: {
    icon: FileText,
    iconColor: 'text-warning-600',
    bgColor: 'bg-warning-100',
  },
  pbr_expiry: {
    icon: Shield,
    iconColor: 'text-error-600',
    bgColor: 'bg-error-100',
  },
  patent_expiry: {
    icon: Shield,
    iconColor: 'text-error-600',
    bgColor: 'bg-error-100',
  },
  declaration_due: {
    icon: Clock,
    iconColor: 'text-primary-600',
    bgColor: 'bg-primary-100',
  },
  certificate_expiry: {
    icon: FileText,
    iconColor: 'text-warning-600',
    bgColor: 'bg-warning-100',
  },
  confirmation_pending: {
    icon: Bell,
    iconColor: 'text-primary-600',
    bgColor: 'bg-primary-100',
  },
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'border-l-error-500 bg-error-50',
  warning: 'border-l-warning-500 bg-warning-50',
  info: 'border-l-primary-500 bg-primary-50',
};

function AlertList({ alerts, onAlertUpdated }: AlertListProps) {
  const timezone = useOrgTimezone();
  const [processingId, setProcessingId] = useState<number | null>(null);

  const handleAcknowledge = async (alertId: number) => {
    setProcessingId(alertId);
    try {
      const response = await cultivarService.acknowledgeAlert(alertId);
      if (response.success) {
        onAlertUpdated();
      }
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDismiss = async (alertId: number) => {
    setProcessingId(alertId);
    try {
      const response = await cultivarService.dismissAlert(alertId);
      if (response.success) {
        onAlertUpdated();
      }
    } catch (err) {
      console.error('Error dismissing alert:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (date: string) => formatDateInTz(date, timezone);

  const getDaysUntil = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (alerts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="h-16 w-16 rounded-full bg-success-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-success-600" />
        </div>
        <p className="text-neutral-600">No active alerts. You're all caught up!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {alerts.map((alert) => {
        const config = ALERT_TYPE_CONFIG[alert.alert_type] || {
          icon: AlertTriangle,
          iconColor: 'text-warning-600',
          bgColor: 'bg-warning-100',
        };
        const Icon = config.icon;
        const daysUntil = getDaysUntil(alert.due_date);
        const isOverdue = daysUntil < 0;

        return (
          <div
            key={alert.id}
            className={`
              flex items-start gap-4 p-4 rounded-lg border-l-4
              ${SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.warning}
            `}
          >
            {/* Icon */}
            <div className={`h-10 w-10 rounded-lg ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`h-5 w-5 ${config.iconColor}`} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-neutral-900">{alert.title}</p>
                  {alert.message && (
                    <p className="text-sm text-neutral-600 mt-1">{alert.message}</p>
                  )}
                </div>

                {/* Urgency Badge */}
                <div className={`
                  flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium
                  ${isOverdue
                    ? 'bg-error-100 text-error-700'
                    : daysUntil <= 7
                    ? 'bg-error-100 text-error-700'
                    : daysUntil <= 30
                    ? 'bg-warning-100 text-warning-700'
                    : 'bg-neutral-100 text-neutral-600'
                  }
                `}>
                  {isOverdue
                    ? `${Math.abs(daysUntil)}d overdue`
                    : daysUntil === 0
                    ? 'Today'
                    : daysUntil === 1
                    ? 'Tomorrow'
                    : `${daysUntil}d remaining`
                  }
                </div>
              </div>

              {/* Due Date */}
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-1.5 text-sm text-neutral-500">
                  <Calendar className="h-4 w-4" />
                  <span>Due: {formatDate(alert.due_date)}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {alert.status === 'pending' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAcknowledge(alert.id)}
                        disabled={processingId === alert.id}
                      >
                        {processingId === alert.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                        Acknowledge
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDismiss(alert.id)}
                        disabled={processingId === alert.id}
                      >
                        <X className="h-4 w-4" />
                        Dismiss
                      </Button>
                    </>
                  )}
                  {alert.status === 'acknowledged' && (
                    <span className="text-xs text-success-600 flex items-center gap-1">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Acknowledged
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default AlertList;
